// ABOUTME: Reads and saves the extended view preference, and holds the words for it, which the schedule page
// ABOUTME: and the legacy Preferences page both offer. One home, so the two cannot drift apart.

import { useMutation } from "@apollo/client/react";
import { GetApplicationContext } from "hooks/apollo/applicationContext/applicationContext.gql";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import UpdateUserPreferences from "hooks/apollo/user/userPreferences.gql";
import { useNotificationState, useSchedulerState } from "hooks/state";
import { useRef } from "react";

export const EXTENDED_VIEW_LABEL = "Extended view";
export const EXTENDED_VIEW_DESCRIPTION = "This enables you to scroll indefinitely on the schedule.";

export default function useExtendedView() {
  // Both waits leave the switch unable to answer for the preference, so they reach the caller as one: until
  // the saved value arrives there is nothing to draw, and a flip sends the opposite of what is drawn.
  const { loading: readingPreference, user } = useApplicationContext();
  const [updateUserPreferencesMutation, { loading: savingPreference }] = useMutation(UpdateUserPreferences);
  const { extendedViewChanged } = useSchedulerState();
  const { setNotification } = useNotificationState();
  const saving = useRef(false);

  const extendedView = Boolean(user?.userPreferences?.extendedView);

  // Turning extended view off has to re-bound the schedule's data range to the visible window, which is what
  // extendedViewChanged does; the scheduler reads the preference itself straight back out of the cache write
  // below (useSchedulerState#getLatestState).
  const updateExtendedView = () => {
    // NDS's Toggle fires onChange twice for one click: the switch's own handler re-clicks the input that
    // just fired. The mutation's loading flag is not set until after both have run, so it cannot stop the
    // second — without this, every flip saved twice.
    if (saving.current) return undefined;
    saving.current = true;

    return updateUserPreferencesMutation({
      onCompleted: ({ updateUserPreferences }) => {
        const { extendedView: saved } = updateUserPreferences.userPreferences;

        saving.current = false;
        extendedViewChanged({ extendedView: saved });
        setNotification({ message: `${EXTENDED_VIEW_LABEL} turned ${saved ? "on" : "off"}`, type: "success" });
      },
      onError: () => {
        saving.current = false;
        setNotification({ message: `${EXTENDED_VIEW_LABEL} could not be saved`, type: "danger" });
      },
      // Written into the cache rather than refetched: the whole application context is a large read, and
      // re-rendering the schedule around it stutters the switch's own animation. Through the query the
      // preference is read by, so the write is the shape the reader expects; a cache without that query
      // answers undefined and is left alone.
      update: (cache, { data }) => {
        cache.updateQuery({ query: GetApplicationContext }, (existing) =>
          existing
            ? {
                applicationContext: {
                  ...existing.applicationContext,
                  user: {
                    ...existing.applicationContext.user,
                    userPreferences: data.updateUserPreferences.userPreferences,
                  },
                },
              }
            : undefined,
        );
      },
      variables: { extendedView: !extendedView },
    });
  };

  return { extendedView, loading: readingPreference || savingPreference, updateExtendedView };
}
